/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import './SavedOrNewCreditCardPicker.scss';

import * as React from 'react';

import {
    Alert,
    Icon,
    Tabs,
    TabPanel,
    Text,
    useBreakpoint,
    useFlags,
    SecurityHelper,
    Spinner,
} from 'sarsaparilla';

import { WALLET_MAXIMUM_CARDS } from 'ui-cart/src/constants';

import { useWalletQuery } from '../../api/queries';
import { NewCreditCardForm, type PRSPaymentPageState } from './NewCreditCardForm';
import { getIsCardExpired, useSavedCreditCardSorter } from './SavedCreditCardCard.utils';
import { SavedCreditCardForm } from './SavedCreditCardForm';
import { UnlockWalletForm } from './UnlockWalletForm';

interface SavedOrNewCreditCardPickerProps {
    showPurchaseGiftCard: boolean;
    updateParentState: (state: Partial<PRSPaymentPageState>) => void;
    storedOnly?: boolean;
    submitButtonText?: string;
    simple?: boolean;
    showBorder?: boolean;
}

export function SavedOrNewCreditCardPicker({
    showPurchaseGiftCard,
    updateParentState,
    storedOnly = false,
    submitButtonText,
    simple = false,
    showBorder = false,
}: SavedOrNewCreditCardPickerProps) {
    const [selectedCardToken, setSelectedCardToken] = React.useState<string | undefined>(
        undefined
    );

    const { isSmAndDown } = useBreakpoint();
    const { enableDigitalWallet, digitalWalletPhaseIi } = useFlags();
    const isCSR = SecurityHelper.isExternalCSRLoggedIn();

    // Variable which will resolve together various boolean states to determine availability
    let walletIsAvailable = enableDigitalWallet;

    // Set unavailable if phase II is not released and we're a CSR
    if (!digitalWalletPhaseIi && isCSR) {
        walletIsAvailable = false;
    }

    // Only attempt to fetch wallet if the feature flag is on
    // if the user is a CSR, the initial fetch should return data.is_locked = true
    const { isLoading, data, isError } = useWalletQuery(walletIsAvailable);

    const sortedSavedCards = useSavedCreditCardSorter(data?.entries || []);

    const hasMaxSavedCards = sortedSavedCards.length >= WALLET_MAXIMUM_CARDS;

    walletIsAvailable = (data?.count || 0) > 0;

    const selectDefault = React.useCallback(() => {
        if (simple) return;
        if (!data?.entries) return;

        const defaultCard = data.entries.find((item) => item.is_default);

        if (defaultCard?.wallet_token) {
            const isExpired = getIsCardExpired(defaultCard.card_expiration_date);

            if (!isExpired) {
                setSelectedCardToken(defaultCard.wallet_token);
            }
        }

        // if there is no default card, select the first card
        if (!defaultCard && data.entries.length > 0) {
            const firstCard = data.entries[0];

            if (firstCard.wallet_token) {
                const isExpired = getIsCardExpired(firstCard.card_expiration_date);

                if (!isExpired) {
                    setSelectedCardToken(firstCard.wallet_token);
                }
            }
        }
    }, [data, simple]);

    // When the data changes, set default selection
    React.useEffect(() => {
        selectDefault();
    }, [selectDefault]);

    const submitCard = (token: string) => {
        const card = data?.entries.find((item) => item.wallet_token === token);

        if (!card) return;

        updateParentState({
            selectedSavedCardToken: token,
            preview: true,
            issuer: card.card_brand,
            number: `xxxxxxxxxxxx${card.card_last_four}`,
            month: card.card_expiration_date.substring(0, 2),
            year: card.card_expiration_date.substring(2, 4),
            nickname: card.name,
        });
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (selectedCardToken) {
            submitCard(selectedCardToken);
        }
    };

    const onCardSelected = (token: string | undefined) => {
        setSelectedCardToken(token);
        if (simple && token) {
            submitCard(token);
        }
    };

    const resetState = () => {
        setSelectedCardToken(undefined);

        updateParentState({
            number: '',
            firstName: '',
            lastName: '',
            month: '',
            year: '',
            cvc: '',
            issuer: '',
            preview: false,
            nickname: '',
            shouldSaveCard: false,
            selectedSavedCardToken: '',
        });

        selectDefault();
    };

    /**
     * If the feature flag is enabled and the query is loading, return a spinner
     */
    if (walletIsAvailable && isLoading) {
        return (
            <div className="pt-6">
                <Spinner />
            </div>
        );
    }

    const isLocked = data?.is_locked === true || data?.is_locked_external === true;
    const hasNoSavedCards = sortedSavedCards.length === 0 && !isLocked;

    /**
     * Show just the new credit card form if:
     * 1. The feature flag is disabled
     * 2. The query did not return data
     * 3. There are no saved cards and the the wallet is not locked
     */
    if (!walletIsAvailable || !data || hasNoSavedCards) {
        if (storedOnly) {
            return null;
        }
        return (
            <NewCreditCardForm
                updateParentState={updateParentState}
                showPurchaseGiftCard={showPurchaseGiftCard}
                isWalletUnavailable={isError}
                hasMaxSavedCards={hasMaxSavedCards}
            />
        );
    }

    if (storedOnly) {
        const allCardsExpired =
            sortedSavedCards.length > 0 &&
            sortedSavedCards.every((card) => getIsCardExpired(card.card_expiration_date));

        if (allCardsExpired) {
            return (
                <div className="cart-saved-credit-card-picker">
                    <Alert type="warning">
                        <Text>
                            All saved cards are expired. Please{' '}
                            <a
                                href="/account/wallet"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                update your payment methods{' '}
                                <Icon iconName="chevron-right" size="sm" />
                                <span className="rec-sr-only">(Opens in new tab)</span>
                            </a>{' '}
                            to use Buy Now.
                        </Text>
                    </Alert>
                </div>
            );
        }

        return (
            <div className="cart-saved-credit-card-picker">
                {isLocked ? (
                    <UnlockWalletForm
                        isLockedExternal={data.is_locked_external === true}
                        cardCount={data?.count}
                        brands={data?.brands}
                        remainingAttempts={data?.unlock_attempts_remaining}
                    />
                ) : (
                    <SavedCreditCardForm
                        onSubmit={onSubmit}
                        sortedSavedCards={sortedSavedCards}
                        selectedCardToken={selectedCardToken}
                        setSelectedCardToken={onCardSelected}
                        submitButtonText={submitButtonText}
                        simple={simple}
                        showBorder={showBorder}
                    />
                )}
            </div>
        );
    }

    return (
        <div className="cart-saved-credit-card-picker">
            <Tabs stretchTabsToFit={isSmAndDown} onChange={resetState}>
                <TabPanel label="Saved Credit Cards">
                    {isLocked ? (
                        <UnlockWalletForm
                            isLockedExternal={data.is_locked_external === true}
                            cardCount={data?.count}
                            brands={data?.brands}
                            remainingAttempts={data?.unlock_attempts_remaining}
                        />
                    ) : (
                        <SavedCreditCardForm
                            onSubmit={onSubmit}
                            sortedSavedCards={sortedSavedCards}
                            selectedCardToken={selectedCardToken}
                            setSelectedCardToken={setSelectedCardToken}
                        />
                    )}
                </TabPanel>
                <TabPanel label="New Credit Card">
                    <NewCreditCardForm
                        updateParentState={updateParentState}
                        showPurchaseGiftCard={showPurchaseGiftCard}
                        hasMaxSavedCards={hasMaxSavedCards}
                    />
                </TabPanel>
            </Tabs>
        </div>
    );
}
